import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Button, ConfirmationModal, FormAllocateModal, LoadingSpinner, QRCodeModal } from '../components'
import { Card, Modal } from '../components/ui'
import { API_BASE_URL, ConfirmationModalMeta, LoadingSpinnerMeta } from '../data'
import type { MatchedVolunteer, PublicForm, PublicFormField, PublicFormFieldType, PublicFormSubmission, PublicFormsMetaProps } from '../data/types'
import { ChevronLeftIcon, DeleteIcon, DownloadIcon, EditIcon, EyeIcon, GripVerticalIcon, PlusIcon, ScanIcon } from '../icons'

type Props = {
    meta: PublicFormsMetaProps
}

type EditableForm = {
    title: string
    slug: string
    description: string
    is_active: boolean
    fields: PublicFormField[]
    allow_export?: boolean
}

type AllocationSelection = {
    eventId: number | null
    areaId: number | null
    shiftId: number | null
}

let fieldIdSequence = 0

const createEmptyField = (): PublicFormField => {
    fieldIdSequence += 1
    return {
        id: `field_${Date.now()}_${fieldIdSequence}`,
        label: '',
        type: 'text',
        required: false,
        options: []
    }
}

const createEmptyForm = (): EditableForm => ({
    title: '',
    slug: '',
    description: '',
    is_active: true,
    fields: [createEmptyField()],
    allow_export: false
})

type SortableFieldCardProps = {
    field: PublicFormField
    fieldNumber: number
    fieldTypeLabels: Record<PublicFormFieldType, string>
    canRemove: boolean
    meta: PublicFormsMetaProps
    onUpdateField: (updates: Partial<PublicFormField>) => void
    onRemoveField: () => void
    onAddOption: () => void
    onUpdateOption: (optionIndex: number, value: string) => void
    onRemoveOption: (optionIndex: number) => void
}

const SortableFieldCard = (props: SortableFieldCardProps) => {
    const { field, fieldNumber, fieldTypeLabels, canRemove, meta, onUpdateField, onRemoveField, onAddOption, onUpdateOption, onRemoveOption } = props
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id as string })

    const style: React.CSSProperties = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${isDragging ? 1.02 : 1})` : undefined,
        transition,
        position: 'relative',
        zIndex: isDragging ? 10 : undefined,
        boxShadow: isDragging ? '0 10px 25px -5px rgba(0,0,0,0.35), 0 8px 10px -6px rgba(0,0,0,0.2)' : undefined
    }

    return (
        <div ref={setNodeRef} style={style} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4 bg-white dark:bg-gray-800">
            <div className="flex items-start gap-3">
                <button
                    type="button"
                    {...attributes}
                    {...listeners}
                    className="mt-1 shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 touch-none"
                    style={{ touchAction: 'none' }}
                    aria-label={`Reorder field ${fieldNumber}`}
                >
                    <GripVerticalIcon className="w-5 h-5" />
                </button>

                <div className="flex-1 space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px_auto_auto] gap-4 items-end">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{meta.fieldLabelLabel}</label>
                            <input className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100" value={field.label} onChange={event => onUpdateField({ label: event.target.value })} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{meta.fieldTypeLabel}</label>
                            <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100" value={field.type} onChange={event => onUpdateField({ type: event.target.value as PublicFormFieldType, options: ['text', 'email'].includes(event.target.value) ? [] : field.options })}>
                                {Object.entries(fieldTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 pb-2">
                            <input type="checkbox" checked={field.required} onChange={event => onUpdateField({ required: event.target.checked })} />
                            {meta.requiredLabel}
                        </label>
                        <Button type="button" variant="outline" color="danger" onClick={onRemoveField} disabled={!canRemove}>{meta.removeButton}</Button>
                    </div>

                    {!['text', 'email'].includes(field.type) && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{meta.optionsLabel}</label>
                                <Button type="button" size="sm" variant="outline" onClick={onAddOption}>{meta.addOptionButton}</Button>
                            </div>
                            {(field.options.length === 0 ? [''] : field.options).map((option, optionIndex) => (
                                <div key={optionIndex} className="flex gap-2">
                                    <input className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100" placeholder={meta.optionPlaceholder} value={option} onChange={event => onUpdateOption(optionIndex, event.target.value)} />
                                    <Button type="button" variant="outline" color="danger" onClick={() => onRemoveOption(optionIndex)}>{meta.removeButton}</Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const parseError = async (response: Response) => {
    const text = await response.text()
    try {
        const parsed = JSON.parse(text)
        if (parsed.errors) {
            return Object.values(parsed.errors).flat().join(' ')
        }
        return parsed.message || text
    } catch {
        return text
    }
}

export const PublicForms = (props: Props) => {
    const { meta } = props
    const [forms, setForms] = useState<PublicForm[]>([])
    const [submissions, setSubmissions] = useState<PublicFormSubmission[]>([])
    const [selectedForm, setSelectedForm] = useState<PublicForm | null>(null)
    const [editingForm, setEditingForm] = useState<EditableForm>(createEmptyForm())
    const [resultsForm, setResultsForm] = useState<PublicForm | null>(null)
    const [formToDelete, setFormToDelete] = useState<PublicForm | null>(null)
    const [qrCodeForm, setQrCodeForm] = useState<PublicForm | null>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [loadingSubmissions, setLoadingSubmissions] = useState(false)
    const [isEditorOpen, setIsEditorOpen] = useState(false)
    const [overrideUrl, setOverrideUrl] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [copiedFormId, setCopiedFormId] = useState<number | null>(null)

    // Volunteer matching (by an email field) for the currently shown responses
    const [matchFieldId, setMatchFieldId] = useState<string>('')
    const [matches, setMatches] = useState<Record<number, MatchedVolunteer[]>>({})
    const [loadingMatches, setLoadingMatches] = useState(false)
    const [selectedMatches, setSelectedMatches] = useState<Record<number, number>>({})
    const [matchModalSubmissionId, setMatchModalSubmissionId] = useState<number | null>(null)
    const [allocateVolunteers, setAllocateVolunteers] = useState<MatchedVolunteer[] | null>(null)
    const [allocationSelection, setAllocationSelection] = useState<AllocationSelection>({
        eventId: null,
        areaId: null,
        shiftId: null
    })

    const fieldTypeLabels: Record<PublicFormFieldType, string> = useMemo(() => ({
        text: meta.textTypeLabel,
        email: meta.emailTypeLabel,
        checkbox: meta.checkboxTypeLabel,
        radio: meta.radioTypeLabel,
        select: meta.selectTypeLabel
    }), [meta])

    useEffect(() => {
        fetchForms()
    }, [])

    const fetchForms = async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await fetch(`${API_BASE_URL}/forms`, { credentials: 'include' })
            if (!response.ok) {
                throw new Error(await parseError(response))
            }
            const result = await response.json()
            setForms(result)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorTitle)
        } finally {
            setLoading(false)
        }
    }

    const openCreateEditor = () => {
        setSelectedForm(null)
        setEditingForm(createEmptyForm())
        setOverrideUrl(false)
        setIsEditorOpen(true)
    }

    const openEditEditor = (form: PublicForm) => {
        setSelectedForm(form)
        setEditingForm({
            title: form.title,
            slug: form.slug,
            description: form.description || '',
            is_active: form.is_active,
            fields: form.fields.map(field => ({ ...field, options: field.options || [] })),
            allow_export: form.allow_export
        })
        setOverrideUrl(false)
        setIsEditorOpen(true)
    }

    const closeEditor = () => {
        setIsEditorOpen(false)
        setSelectedForm(null)
        setEditingForm(createEmptyForm())
        setOverrideUrl(false)
    }

    const updateField = (fieldId: string | undefined, updates: Partial<PublicFormField>) => {
        setEditingForm(prev => ({
            ...prev,
            fields: prev.fields.map(field => field.id === fieldId ? { ...field, ...updates } : field)
        }))
    }

    const addField = () => {
        setEditingForm(prev => ({ ...prev, fields: [...prev.fields, createEmptyField()] }))
    }

    const removeField = (fieldId: string | undefined) => {
        setEditingForm(prev => ({
            ...prev,
            fields: prev.fields.length === 1 ? prev.fields : prev.fields.filter(field => field.id !== fieldId)
        }))
    }

    const updateOption = (fieldId: string | undefined, optionIndex: number, value: string) => {
        const field = editingForm.fields.find(field => field.id === fieldId)
        if (!field) return
        const options = [...field.options]
        options[optionIndex] = value
        updateField(fieldId, { options })
    }

    const addOption = (fieldId: string | undefined) => {
        const field = editingForm.fields.find(field => field.id === fieldId)
        if (!field) return
        updateField(fieldId, { options: [...field.options, ''] })
    }

    const removeOption = (fieldId: string | undefined, optionIndex: number) => {
        const field = editingForm.fields.find(field => field.id === fieldId)
        if (!field) return
        updateField(fieldId, { options: field.options.filter((_, index) => index !== optionIndex) })
    }

    const fieldDragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

    const handleFieldDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (!over || active.id === over.id) return
        setEditingForm(prev => {
            const oldIndex = prev.fields.findIndex(field => field.id === active.id)
            const newIndex = prev.fields.findIndex(field => field.id === over.id)
            if (oldIndex === -1 || newIndex === -1) return prev
            return { ...prev, fields: arrayMove(prev.fields, oldIndex, newIndex) }
        })
    }

    const saveForm = async () => {
        setSaving(true)
        setError(null)
        try {
            const payload: Partial<EditableForm> = {
                title: editingForm.title,
                description: editingForm.description,
                is_active: editingForm.is_active,
                fields: editingForm.fields.map(field => ({
                    ...field,
                    label: field.label.trim(),
                    options: ['text', 'email'].includes(field.type) ? [] : field.options.map(option => option.trim()).filter(Boolean)
                })),
                allow_export: editingForm.allow_export,
            }

            if (overrideUrl && editingForm.slug.trim()) {
                payload.slug = editingForm.slug.trim()
            }

            const response = await fetch(`${API_BASE_URL}/forms${selectedForm ? `/${selectedForm.id}` : ''}`, {
                method: selectedForm ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            })

            if (!response.ok) {
                throw new Error(await parseError(response))
            }

            const result = await response.json()
            if (result.model) {
                setForms(prev => selectedForm
                    ? prev.map(form => form.id === result.model.id ? result.model : form)
                    : [result.model, ...prev]
                )
            }
            closeEditor()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorTitle)
        } finally {
            setSaving(false)
        }
    }

    const deleteForm = async () => {
        if (!formToDelete) return

        setDeleting(true)
        setError(null)
        try {
            const response = await fetch(`${API_BASE_URL}/forms/${formToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Accept': 'application/json' },
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error(await parseError(response))
            }

            setForms(prev => prev.filter(form => form.id !== formToDelete.id))
            if (resultsForm?.id === formToDelete.id) {
                setResultsForm(null)
                setSubmissions([])
            }
            setFormToDelete(null)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorTitle)
        } finally {
            setDeleting(false)
        }
    }

    const resetMatching = () => {
        setMatchFieldId('')
        setMatches({})
        setSelectedMatches({})
        setMatchModalSubmissionId(null)
    }

    const loadSubmissions = async (form: PublicForm) => {
        if (resultsForm?.id === form.id) {
            setResultsForm(null)
            setSubmissions([])
            resetMatching()
            return
        }

        setResultsForm(form)
        setLoadingSubmissions(true)
        setError(null)
        resetMatching()
        try {
            const response = await fetch(`${API_BASE_URL}/forms/${form.id}/submissions`, { credentials: 'include' })
            if (!response.ok) {
                throw new Error(await parseError(response))
            }
            const result = await response.json()
            setSubmissions(result)

            // Default volunteer matching to the first email field, if any
            const firstEmailField = form.fields.find(field => field.type === 'email')
            if (firstEmailField?.id) {
                setMatchFieldId(firstEmailField.id)
                fetchMatches(form, firstEmailField.id)
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorTitle)
        } finally {
            setLoadingSubmissions(false)
        }
    }

    const fetchMatches = async (form: PublicForm, emailFieldId: string) => {
        setLoadingMatches(true)
        try {
            const response = await fetch(`${API_BASE_URL}/forms/${form.id}/volunteer-matches`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email_field_id: emailFieldId })
            })
            if (!response.ok) {
                throw new Error(await parseError(response))
            }
            const result = await response.json()
            // Backend returns an object keyed by submission id (or [] when there are none)
            setMatches(Array.isArray(result) ? {} : result)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorTitle)
            setMatches({})
        } finally {
            setLoadingMatches(false)
        }
    }

    const handleMatchFieldChange = (form: PublicForm, emailFieldId: string) => {
        setMatchFieldId(emailFieldId)
        setSelectedMatches({})
        if (emailFieldId) {
            fetchMatches(form, emailFieldId)
        } else {
            setMatches({})
        }
    }

    // Resolve the volunteer to display for a submission: an explicit choice, or the sole match
    const resolveMatch = (submissionId: number): MatchedVolunteer | null => {
        const candidates = matches[submissionId] || []
        if (candidates.length === 0) return null
        const chosenCsid = selectedMatches[submissionId]
        if (chosenCsid !== undefined) {
            return candidates.find(volunteer => volunteer.csid === chosenCsid) || null
        }
        return candidates.length === 1 ? candidates[0] : null
    }

    const getIdentifiedVolunteers = (): MatchedVolunteer[] => {
        const identified = new Map<number, MatchedVolunteer>()
        submissions.forEach(submission => {
            const volunteer = resolveMatch(submission.id)
            if (volunteer) {
                identified.set(volunteer.csid, volunteer)
            }
        })
        return Array.from(identified.values())
    }

    const copyPublicUrl = async (form: PublicForm) => {
        await navigator.clipboard.writeText(`${window.location.origin}/forms/${form.slug}`)
        setCopiedFormId(form.id)
        window.setTimeout(() => setCopiedFormId(null), 1500)
    }

    const renderAnswer = (answer: string | string[] | undefined) => {
        if (Array.isArray(answer)) {
            return answer.join(', ')
        }
        return answer || ''
    }

    const renderMatchCell = (submission: PublicFormSubmission) => {
        if (loadingMatches) {
            return <span className="text-gray-400 dark:text-gray-500">{meta.matchingLabel}</span>
        }

        const candidates = matches[submission.id] || []
        if (candidates.length === 0) {
            return <span className="text-gray-400 dark:text-gray-500">{meta.noMatchLabel}</span>
        }

        const resolved = resolveMatch(submission.id)
        if (resolved) {
            return (
                <div className="flex flex-col gap-1">
                    <Link to={`/volunteer/${resolved.csid}/profile`} className="font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300">
                        {resolved.first_name} {resolved.last_name}
                    </Link>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{resolved.email}</span>
                    {candidates.length > 1 && (
                        <button type="button" onClick={() => setMatchModalSubmissionId(submission.id)} className="self-start text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 underline">
                            {meta.changeMatchButton}
                        </button>
                    )}
                    <Button size="sm" variant="outline" color="primary" className="self-start mt-1" onClick={() => setAllocateVolunteers([resolved])}>
                        {meta.approveAndAllocateButton}
                    </Button>
                </div>
            )
        }

        // More than one volunteer matched and none chosen yet
        return (
            <Button size="sm" variant="outline" color="primary" onClick={() => setMatchModalSubmissionId(submission.id)}>
                {meta.selectMatchButton(candidates.length)}
            </Button>
        )
    }

    const handleExportSubmissions = async (form: PublicForm) => {
        const response = await fetch(`${API_BASE_URL}/forms/${form.id}/submissions`, { credentials: 'include' })
        if (!response.ok) throw new Error('Failed to fetch submissions')
        const submissions: PublicFormSubmission[] = await response.json()

        const escapeCsv = (val: string | number | null | undefined) => {
            if (val === null || val === undefined) return ''
            const s = String(val)
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
        }

        const headers = [...form.fields.map(f => f.label), 'Submitted At']
        const rows = submissions.map(sub => [
            ...form.fields.map(f => {
                const value = sub.answers[f.id]
                return Array.isArray(value) ? value.join('; ') : (value ?? '')
            }),
            new Date(sub.created_at).toLocaleString(),
        ])

        const csv = [headers, ...rows].map(row => row.map(escapeCsv).join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${form.slug}-submissions.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className="mb-10">
                        <div className="mb-3">
                            <Link to="/admin" className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300">
                                <ChevronLeftIcon className="w-5 h-5 me-2" />
                                {meta.backButton}
                            </Link>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{meta.title}</h1>
                                <p className="mt-2 text-gray-600 dark:text-gray-400">{meta.description}</p>
                            </div>
                            <Button onClick={openCreateEditor} className="self-start sm:self-auto">
                                <PlusIcon className="w-5 h-5 me-2" />
                                {meta.createButton}
                            </Button>
                        </div>
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {error && (
                        <div className="mb-6 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                            <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                            <p>{error}</p>
                        </div>
                    )}

                    {!loading && forms.length === 0 && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            <p className="text-xl">{meta.noFormsMessage}</p>
                        </div>
                    )}

                    {!loading && forms.length > 0 && (
                        <div className="space-y-6">
                            {forms.map(form => (
                                <Card key={form.id}>
                                    <div className="p-6 space-y-4">
                                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                                            <div className='flex-1 min-w-0'>
                                                <div className="flex flex-wrap items-center gap-3">
                                                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{form.title}</h2>
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${form.is_active ? 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-200' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
                                                        {form.is_active ? meta.activeLabel : meta.inactiveLabel}
                                                    </span>
                                                </div>
                                                {form.description && <p className="mt-2 text-gray-600 dark:text-gray-400">{form.description}</p>}
                                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{form.submissions_count || 0} {meta.submissionsCountLabel}</p>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                <Button variant="outline" color="neutral" onClick={() => copyPublicUrl(form)}>
                                                    {copiedFormId === form.id ? meta.copiedText : meta.copyLinkButton}
                                                </Button>
                                                <Button variant="outline" color="neutral" onClick={() => setQrCodeForm(form)}>
                                                    <ScanIcon className="w-4 h-4" />
                                                    {meta.showQrCodeButton}
                                                </Button>
                                                <Button variant="outline" color="primary" onClick={() => loadSubmissions(form)}>
                                                    <EyeIcon className="w-4 h-4" />
                                                    {resultsForm?.id === form.id ? meta.hideResultsButton : meta.viewResultsButton}
                                                </Button>
                                                {form.allow_export && (
                                                    <Button variant="outline" color="primary" onClick={() => handleExportSubmissions(form)}>
                                                        <DownloadIcon className="w-4 h-4" />
                                                        {meta.exportResultButton}
                                                    </Button>
                                                )}
                                                <Button variant="icon" color="primary" onClick={() => openEditEditor(form)} title={meta.editButton}>
                                                    <EditIcon className="w-5 h-5" />
                                                </Button>
                                                <Button variant="icon" color="danger" onClick={() => setFormToDelete(form)} title={meta.deleteButton}>
                                                    <DeleteIcon className="w-5 h-5" />
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="rounded-md bg-gray-50 dark:bg-gray-900 p-3 text-sm text-gray-700 dark:text-gray-300 break-all">
                                            <span className="font-medium">{meta.publicUrlLabel}: </span>
                                            {window.location.origin}/forms/{form.slug}
                                        </div>

                                        {resultsForm?.id === form.id && (() => {
                                            const emailFields = form.fields.filter(field => field.type === 'email' && field.id)
                                            const showMatches = emailFields.length > 0
                                            const identifiedVolunteers = getIdentifiedVolunteers()
                                            return (
                                                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">{meta.submissionsTitle}</h3>
                                                    {loadingSubmissions && <LoadingSpinner meta={LoadingSpinnerMeta} />}
                                                    {!loadingSubmissions && submissions.length === 0 && <p className="text-gray-500 dark:text-gray-400">{meta.noSubmissionsMessage}</p>}
                                                    {!loadingSubmissions && submissions.length > 0 && (
                                                        <>
                                                            {showMatches && (
                                                                <div className="mb-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                                                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{meta.matchByLabel}:</label>
                                                                        <select
                                                                            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                                                                            value={matchFieldId}
                                                                            onChange={event => handleMatchFieldChange(form, event.target.value)}
                                                                        >
                                                                            {emailFields.map(field => (
                                                                                <option key={field.id} value={field.id}>{field.label}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        color="primary"
                                                                        disabled={identifiedVolunteers.length === 0 || loadingMatches}
                                                                        onClick={() => setAllocateVolunteers(identifiedVolunteers)}
                                                                    >
                                                                        {meta.approveAndAllocateButton} ({identifiedVolunteers.length})
                                                                    </Button>
                                                                </div>
                                                            )}
                                                            <div className="overflow-x-auto">
                                                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                                    <thead>
                                                                        <tr>
                                                                            <th className="px-4 py-3 text-start text-sm font-semibold text-gray-900 dark:text-gray-100">{meta.submittedAtLabel}</th>
                                                                            {form.fields.map(field => (
                                                                                <th key={field.id} className="px-4 py-3 text-start text-sm font-semibold text-gray-900 dark:text-gray-100">{field.label}</th>
                                                                            ))}
                                                                            {showMatches && (
                                                                                <th className="px-4 py-3 text-start text-sm font-semibold text-gray-900 dark:text-gray-100">{meta.matchedVolunteerColumn}</th>
                                                                            )}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                                                        {submissions.map(submission => (
                                                                            <tr key={submission.id}>
                                                                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{new Date(submission.created_at).toLocaleString()}</td>
                                                                                {form.fields.map(field => (
                                                                                    <td key={field.id} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{renderAnswer(submission.answers[field.id || ''])}</td>
                                                                                ))}
                                                                                {showMatches && (
                                                                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{renderMatchCell(submission)}</td>
                                                                                )}
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )
                                        })()}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Modal
                isOpen={isEditorOpen}
                onClose={closeEditor}
                title={selectedForm ? meta.editModalTitle : meta.createModalTitle}
                maxWidth="5xl"
                footer={
                    <div className="flex flex-row-reverse gap-3">
                        <Button onClick={saveForm} disabled={saving}>{saving ? meta.savingButton : meta.saveButton}</Button>
                        <Button variant="outline" color="neutral" onClick={closeEditor} disabled={saving}>{ConfirmationModalMeta.cancelButton}</Button>
                    </div>
                }
            >
                <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
                    <div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{meta.titleLabel}</label>
                            <input className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100" value={editingForm.title} onChange={event => setEditingForm(prev => ({ ...prev, title: event.target.value }))} />
                        </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            <input type="checkbox" checked={overrideUrl} onChange={event => setOverrideUrl(event.target.checked)} />
                            {meta.overrideUrlLabel}
                        </label>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{meta.overrideUrlHelpText}</p>
                        {overrideUrl && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{meta.slugLabel}</label>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                    <span className="text-sm text-gray-500 dark:text-gray-400 break-all">{window.location.origin}/forms/</span>
                                    <input className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100" placeholder={meta.customUrlPlaceholder} value={editingForm.slug} onChange={event => setEditingForm(prev => ({ ...prev, slug: event.target.value }))} />
                                </div>
                            </div>
                        )}
                    </div>
                    {/* Export checkbox */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            <input type="checkbox" checked={editingForm.allow_export ?? false} onChange={event => setEditingForm((prev) => ({ ...prev, allow_export: event.target.checked }))} />
                            {meta.exportLabel}
                        </label>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{meta.descriptionLabel}</label>
                        <textarea className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100" rows={3} value={editingForm.description} onChange={event => setEditingForm(prev => ({ ...prev, description: event.target.value }))} />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={editingForm.is_active} onChange={event => setEditingForm(prev => ({ ...prev, is_active: event.target.checked }))} />
                        {meta.activeFormLabel}
                    </label>

                    <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{meta.fieldsLabel}</h3>
                        <Button type="button" variant="outline" onClick={addField}>
                            <PlusIcon className="w-4 h-4" />
                            {meta.addFieldButton}
                        </Button>
                    </div>

                    <DndContext sensors={fieldDragSensors} collisionDetection={closestCenter} onDragEnd={handleFieldDragEnd}>
                        <SortableContext items={editingForm.fields.map(field => field.id as string)} strategy={verticalListSortingStrategy}>
                            <div className="space-y-4">
                                {editingForm.fields.map((field, fieldIndex) => (
                                    <SortableFieldCard
                                        key={field.id || fieldIndex}
                                        field={field}
                                        fieldNumber={fieldIndex + 1}
                                        fieldTypeLabels={fieldTypeLabels}
                                        canRemove={editingForm.fields.length > 1}
                                        meta={meta}
                                        onUpdateField={updates => updateField(field.id, updates)}
                                        onRemoveField={() => removeField(field.id)}
                                        onAddOption={() => addOption(field.id)}
                                        onUpdateOption={(optionIndex, value) => updateOption(field.id, optionIndex, value)}
                                        onRemoveOption={optionIndex => removeOption(field.id, optionIndex)}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            </Modal>

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={formToDelete !== null}
                onConfirm={deleteForm}
                onCancel={() => setFormToDelete(null)}
                title={meta.deleteTitle}
                message={meta.deleteMessage}
                isLoading={deleting}
            />

            <QRCodeModal
                meta={meta}
                isOpen={qrCodeForm !== null}
                onClose={() => setQrCodeForm(null)}
                url={qrCodeForm ? `${window.location.origin}/forms/${qrCodeForm.slug}` : ''}
                slug={qrCodeForm?.slug ?? 'form'}
                copied={qrCodeForm !== null && copiedFormId === qrCodeForm.id}
                onCopyLink={() => qrCodeForm && copyPublicUrl(qrCodeForm)}
            />

            {matchModalSubmissionId !== null && (
                <Modal
                    isOpen={true}
                    onClose={() => setMatchModalSubmissionId(null)}
                    title={meta.matchModalTitle}
                    maxWidth="lg"
                    footer={
                        <div className="flex justify-end">
                            <Button variant="outline" color="neutral" onClick={() => setMatchModalSubmissionId(null)}>{meta.matchCloseButton}</Button>
                        </div>
                    }
                >
                    <div className="p-6 space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">{meta.matchModalDescription}</p>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead>
                                    <tr>
                                        <th className="px-3 py-2 text-start text-xs font-semibold text-gray-900 dark:text-gray-100">{meta.matchIdColumn}</th>
                                        <th className="px-3 py-2 text-start text-xs font-semibold text-gray-900 dark:text-gray-100">{meta.matchNameColumn}</th>
                                        <th className="px-3 py-2 text-start text-xs font-semibold text-gray-900 dark:text-gray-100">{meta.matchEmailColumn}</th>
                                        <th className="px-3 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {(matches[matchModalSubmissionId] || []).map(volunteer => (
                                        <tr key={volunteer.csid}>
                                            <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{volunteer.csid}</td>
                                            <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{volunteer.first_name} {volunteer.last_name}</td>
                                            <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{volunteer.email}</td>
                                            <td className="px-3 py-2 text-sm text-end">
                                                <Button
                                                    size="sm"
                                                    color="primary"
                                                    onClick={() => {
                                                        setSelectedMatches(prev => ({ ...prev, [matchModalSubmissionId]: volunteer.csid }))
                                                        setMatchModalSubmissionId(null)
                                                    }}
                                                >
                                                    {meta.matchSelectButton}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Modal>
            )}

            {allocateVolunteers !== null && (
                <FormAllocateModal
                    meta={meta}
                    isOpen={true}
                    onClose={() => setAllocateVolunteers(null)}
                    volunteers={allocateVolunteers}
                    allocationSelection={allocationSelection}
                    onAllocationSelectionChange={setAllocationSelection}
                    onSuccess={() => setAllocateVolunteers(null)}
                />
            )}
        </Fragment>
    )
}
